import { useEffect, useState, useRef, useContext } from "react";
import api from "../../api/client";
import PortalLayout from "../layout/PortalLayout";
import { AuthContext } from "../../auth/AuthContext";
import { Toast } from "primereact/toast";

const GENDER_OPTIONS = [
  { label: "Male",   value: "male"   },
  { label: "Female", value: "female" },
  { label: "Other",  value: "other"  },
];

const BLOOD_GROUPS = ["A+","A−","B+","B−","AB+","AB−","O+","O−"];

function formatDate(iso) {
  if (!iso) return "";
  return iso.split("T")[0];
}

function Section({ title, icon, children }) {
  return (
    <div className="ph-card">
      <div className="ph-card-header">
        <div className="ph-card-title">
          <i className={`pi ${icon}`} />
          {title}
        </div>
      </div>
      <div style={{ padding: "18px 20px" }}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)",
        letterSpacing: "0.5px", textTransform: "uppercase",
        display: "block", marginBottom: 6,
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", fontSize: 13.5, padding: "9px 12px",
  border: "1px solid var(--border-color)", borderRadius: 10,
  fontFamily: "inherit", color: "var(--text-primary)",
  background: "var(--surface-card)", outline: "none",
  boxSizing: "border-box", transition: "border-color 0.15s",
};

const readonlyStyle = {
  ...inputStyle,
  background: "var(--surface-bg)",
  color: "var(--text-secondary)",
  cursor: "not-allowed",
};

// ── Phone change modal ────────────────────────────────────────────────────────
function PhoneChangeModal({ currentPhone, onClose, onSuccess, toast }) {
  const [step,        setStep]        = useState("enter"); // "enter" | "otp"
  const [newPhone,    setNewPhone]    = useState("");
  const [otp,         setOtp]         = useState("");
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [counter,     setCounter]     = useState(0);
  const [claimPending, setClaimPending] = useState(false);

  useEffect(() => {
    if (counter <= 0) return;
    const t = setTimeout(() => setCounter(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [counter]);

  const fullPhone = "0" + newPhone;

  const sendOtp = async () => {
    setError("");
    if (newPhone.length !== 9) {
      setError("Enter a valid 9-digit number"); return;
    }
    if (fullPhone === currentPhone) {
      setError("This is already your current number"); return;
    }
    setLoading(true);
    try {
      const res = await api.post("auth/phone/send/", { phone: fullPhone });
      setStep("otp");
      setCounter(60);
      // Show claim warning early if detected on send
      if (res.data.purpose === "phone_claim") {
        setError("");
      }
    } catch (err) {
      setError(err.response?.data?.error ?? "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const confirmOtp = async () => {
    setError("");
    if (otp.length !== 6) { setError("Enter all 6 digits"); return; }
    setLoading(true);
    try {
      const res = await api.post("auth/phone/confirm/", { phone: fullPhone, otp });
      if (res.data.claim_initiated) {
        setClaimPending(true);
        return;
      }
      toast.current?.show({
        severity: "success",
        summary: "Phone number updated",
        detail: `Your number is now ${fullPhone}`,
        life: 4000,
      });
      onSuccess(fullPhone);
    } catch (err) {
      setError(err.response?.data?.error ?? "Invalid or expired OTP");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("auth/phone/send/", { phone: fullPhone });
      setCounter(60);
      setOtp("");
      setError("");
    } catch {
      setError("Failed to resend OTP");
    }
  };

  // Overlay + modal
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }}>
      <div style={{
        background: "var(--surface-card)", borderRadius: 16,
        padding: "28px 28px 24px", width: "100%", maxWidth: 400,
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 16, color: "var(--text-primary)" }}>
            <i className="pi pi-mobile" style={{ marginRight: 8, color: "var(--brand-primary)" }} />
            Change phone number
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", fontSize: 18, padding: 4,
          }}>
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Claim pending state */}
        {claimPending ? (
          <div style={{
            background: "#fffbeb", border: "1px solid #f59e0b",
            borderRadius: 10, padding: "14px 16px",
            fontSize: 13, color: "#92400e", lineHeight: 1.6,
          }}>
            <i className="pi pi-clock" style={{ marginRight: 8, color: "#f59e0b" }} />
            <strong>Transfer initiated</strong><br />
            This number is linked to another account. If uncontested within 48 hours,
            it will be transferred to your account automatically.
          </div>
        ) : step === "enter" ? (
          <>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
              Current number: <strong>{currentPhone || "Not set"}</strong>
            </p>

            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: 8, padding: "8px 12px",
                fontSize: 13, color: "#dc2626", marginBottom: 14,
              }}>
                {error}
              </div>
            )}

            <label style={{
              fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)",
              letterSpacing: "0.5px", textTransform: "uppercase",
              display: "block", marginBottom: 6,
            }}>
              New mobile number
            </label>
            <div style={{
              display: "flex", border: "1.5px solid var(--border-color)",
              borderRadius: 10, overflow: "hidden",
            }}>
              <div style={{
                padding: "10px 12px", background: "var(--surface-bg)",
                fontSize: 13, fontWeight: 600, color: "var(--text-secondary)",
                borderRight: "1px solid var(--border-color)", whiteSpace: "nowrap",
              }}>
                🇱🇰 +94
              </div>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={9}
                value={newPhone}
                onChange={e => setNewPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                onKeyDown={e => e.key === "Enter" && sendOtp()}
                placeholder="7X XXX XXXX"
                style={{
                  flex: 1, border: "none", outline: "none",
                  padding: "10px 12px", fontSize: 14,
                  fontFamily: "inherit", background: "white",
                  letterSpacing: "1px",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={onClose} style={{
                flex: 1, padding: "10px", borderRadius: 10, fontSize: 13,
                background: "none", border: "1px solid var(--border-color)",
                color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit",
              }}>
                Cancel
              </button>
              <button onClick={sendOtp} disabled={loading} style={{
                flex: 1, padding: "10px", borderRadius: 10, fontSize: 13,
                background: "var(--brand-primary)", color: "white",
                border: "none", cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit", fontWeight: 600,
                opacity: loading ? 0.75 : 1,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                {loading
                  ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 13 }} /> Sending…</>
                  : <><i className="pi pi-send" style={{ fontSize: 13 }} /> Send OTP</>
                }
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
              Enter the 6-digit code sent to <strong>0{newPhone}</strong>
            </p>

            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: 8, padding: "8px 12px",
                fontSize: 13, color: "#dc2626", marginBottom: 14,
              }}>
                {error}
              </div>
            )}

            <input
              type="tel"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={e => e.key === "Enter" && confirmOtp()}
              placeholder="_ _ _ _ _ _"
              style={{
                ...inputStyle,
                fontSize: 22, fontWeight: 700, letterSpacing: "12px",
                textAlign: "center",
              }}
            />

            <div style={{ textAlign: "center", marginTop: 10, fontSize: 12.5, color: "var(--text-muted)" }}>
              {counter > 0 ? (
                <>Resend in <strong>{counter}s</strong></>
              ) : (
                <span
                  onClick={resend}
                  style={{ color: "var(--brand-primary)", cursor: "pointer", fontWeight: 600 }}
                >
                  Resend code
                </span>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={() => { setStep("enter"); setOtp(""); setError(""); }} style={{
                flex: 1, padding: "10px", borderRadius: 10, fontSize: 13,
                background: "none", border: "1px solid var(--border-color)",
                color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit",
              }}>
                Back
              </button>
              <button onClick={confirmOtp} disabled={loading || otp.length !== 6} style={{
                flex: 1, padding: "10px", borderRadius: 10, fontSize: 13,
                background: "var(--brand-primary)", color: "white",
                border: "none", fontFamily: "inherit", fontWeight: 600,
                cursor: (loading || otp.length !== 6) ? "not-allowed" : "pointer",
                opacity: (loading || otp.length !== 6) ? 0.65 : 1,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                {loading
                  ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 13 }} /> Verifying…</>
                  : <><i className="pi pi-shield" style={{ fontSize: 13 }} /> Verify & Update</>
                }
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Profile() {
  const toast = useRef(null);

  const [profile,          setProfile]          = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [editing,          setEditing]          = useState(false);
  const [form,             setForm]             = useState({});
  const [showPhoneModal,   setShowPhoneModal]   = useState(false);

  useEffect(() => {
    api.get("me/")
      .then(res => { setProfile(res.data); setForm(res.data); })
      .catch(() => toast.current?.show({
        severity: "error", summary: "Failed to load profile", life: 3000,
      }))
      .finally(() => setLoading(false));
  }, []);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("me/", form);
      setProfile(res.data);
      setForm(res.data);
      setEditing(false);
      toast.current.show({ severity: "success", summary: "Profile updated", life: 3000 });
    } catch (err) {
      const detail = err.response?.data?.error ?? "Could not save changes.";
      toast.current.show({ severity: "error", summary: "Save failed", detail, life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setForm(profile); setEditing(false); };

  // Called by modal on successful phone change
  const onPhoneChanged = (newPhone) => {
    setProfile(p => ({ ...p, phone: newPhone }));
    setForm(f => ({ ...f, phone: newPhone }));
    setShowPhoneModal(false);
  };

  const initials = profile
    ? [profile.first_name?.[0], profile.last_name?.[0]].filter(Boolean).join("").toUpperCase()
    : "…";

  if (loading) {
    return (
      <PortalLayout>
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, color: "var(--brand-primary)" }} />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Toast ref={toast} position="top-center" />

      {showPhoneModal && (
        <PhoneChangeModal
          currentPhone={profile.phone}
          onClose={() => setShowPhoneModal(false)}
          onSuccess={onPhoneChanged}
          toast={toast}
        />
      )}

      {/* Page header */}
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your personal and medical information</p>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500,
            background: "var(--brand-primary)", color: "white",
            border: "none", cursor: "pointer", fontFamily: "inherit",
          }}>
            <i className="pi pi-pencil" style={{ fontSize: 12 }} />
            Edit profile
          </button>
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={cancel} style={{
              padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500,
              background: "none", color: "var(--text-secondary)",
              border: "1px solid var(--border-color)", cursor: "pointer", fontFamily: "inherit",
            }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500,
              background: "var(--brand-primary)", color: "white",
              border: "none", cursor: saving ? "not-allowed" : "pointer",
              fontFamily: "inherit", opacity: saving ? 0.7 : 1,
            }}>
              <i className={`pi ${saving ? "pi-spin pi-spinner" : "pi-check"}`} style={{ fontSize: 12 }} />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>

      <div className="portal-grid-2" style={{ alignItems: "start", gap: 20 }}>

        {/* ── Left column ──────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Section title="Personal Information" icon="pi-user">

            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 22 }}>
              <div style={{
                width: 60, height: 60, borderRadius: 16, flexShrink: 0,
                background: "linear-gradient(135deg, var(--brand-primary), var(--brand-teal))",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "white", fontSize: 22, fontWeight: 600,
                fontFamily: "var(--font-display)",
              }}>
                {initials}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                  {profile.first_name} {profile.last_name}
                </div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
                  {profile.phone || "No phone linked"}
                </div>
              </div>
            </div>

            <div className="portal-grid-2" style={{ gap: 14 }}>
              <Field label="First name">
                <input
                  style={editing ? inputStyle : readonlyStyle}
                  value={form.first_name ?? ""}
                  readOnly={!editing}
                  onChange={e => set("first_name", e.target.value)}
                  onFocus={e => editing && (e.target.style.borderColor = "var(--brand-primary)")}
                  onBlur={e => e.target.style.borderColor = "var(--border-color)"}
                />
              </Field>
              <Field label="Last name">
                <input
                  style={editing ? inputStyle : readonlyStyle}
                  value={form.last_name ?? ""}
                  readOnly={!editing}
                  onChange={e => set("last_name", e.target.value)}
                  onFocus={e => editing && (e.target.style.borderColor = "var(--brand-primary)")}
                  onBlur={e => e.target.style.borderColor = "var(--border-color)"}
                />
              </Field>
            </div>

            {/* Phone field with change button */}
            <Field label="Phone number">
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  style={{ ...readonlyStyle, flex: 1 }}
                  value={form.phone ?? "Not set"}
                  readOnly
                />
                <button
                  onClick={() => setShowPhoneModal(true)}
                  style={{
                    padding: "9px 14px", borderRadius: 10, fontSize: 12.5,
                    fontWeight: 600, border: "1px solid var(--border-color)",
                    background: "var(--surface-bg)", color: "var(--brand-primary)",
                    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: 5,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--brand-primary)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border-color)"}
                >
                  <i className="pi pi-pencil" style={{ fontSize: 11 }} />
                  Change
                </button>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4 }}>
                OTP verification required to change your number
              </div>
            </Field>

            <Field label="Email address">
              <input
                type="email"
                style={editing ? inputStyle : readonlyStyle}
                value={form.email ?? ""}
                readOnly={!editing}
                placeholder="your@email.com"
                onChange={e => set("email", e.target.value)}
                onFocus={e => editing && (e.target.style.borderColor = "var(--brand-primary)")}
                onBlur={e => e.target.style.borderColor = "var(--border-color)"}
              />
            </Field>

            <div className="portal-grid-2" style={{ gap: 14 }}>
              <Field label="Date of birth">
                <input
                  type="date"
                  style={editing ? inputStyle : readonlyStyle}
                  value={formatDate(form.date_of_birth) ?? ""}
                  readOnly={!editing}
                  onChange={e => set("date_of_birth", e.target.value)}
                  onFocus={e => editing && (e.target.style.borderColor = "var(--brand-primary)")}
                  onBlur={e => e.target.style.borderColor = "var(--border-color)"}
                />
              </Field>
              <Field label="Gender">
                {editing ? (
                  <select
                    value={form.gender ?? ""}
                    onChange={e => set("gender", e.target.value)}
                    style={{ ...inputStyle, cursor: "pointer" }}
                    onFocus={e => e.target.style.borderColor = "var(--brand-primary)"}
                    onBlur={e => e.target.style.borderColor = "var(--border-color)"}
                  >
                    <option value="">Select…</option>
                    {GENDER_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    style={readonlyStyle}
                    value={GENDER_OPTIONS.find(o => o.value === form.gender)?.label ?? "—"}
                    readOnly
                  />
                )}
              </Field>
            </div>

          </Section>
        </div>

        {/* ── Right column ─────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Section title="Medical Information" icon="pi-heart">

            <Field label="Blood group">
              {editing ? (
                <select
                  value={form.blood_group ?? ""}
                  onChange={e => set("blood_group", e.target.value)}
                  style={{ ...inputStyle, cursor: "pointer" }}
                  onFocus={e => e.target.style.borderColor = "var(--brand-primary)"}
                  onBlur={e => e.target.style.borderColor = "var(--border-color)"}
                >
                  <option value="">Not specified</option>
                  {BLOOD_GROUPS.map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {form.blood_group ? (
                    <span style={{
                      background: "#fee2e2", color: "#b91c1c",
                      fontWeight: 700, fontSize: 15,
                      padding: "4px 14px", borderRadius: 8,
                      fontFamily: "var(--font-display)",
                    }}>
                      {form.blood_group}
                    </span>
                  ) : (
                    <span style={{ color: "var(--text-muted)", fontSize: 13 }}>Not specified</span>
                  )}
                </div>
              )}
            </Field>

            <Field label="Known allergies">
              <textarea
                rows={3}
                style={{ ...(editing ? inputStyle : readonlyStyle), resize: "vertical" }}
                value={form.allergies ?? ""}
                readOnly={!editing}
                placeholder={editing ? "e.g. Penicillin, Aspirin, Peanuts…" : ""}
                onChange={e => set("allergies", e.target.value)}
                onFocus={e => editing && (e.target.style.borderColor = "var(--brand-primary)")}
                onBlur={e => e.target.style.borderColor = "var(--border-color)"}
              />
              {!editing && !form.allergies && (
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>None recorded</span>
              )}
            </Field>

            <Field label="Chronic conditions">
              <textarea
                rows={3}
                style={{ ...(editing ? inputStyle : readonlyStyle), resize: "vertical" }}
                value={form.chronic_conditions ?? ""}
                readOnly={!editing}
                placeholder={editing ? "e.g. Type 2 Diabetes, Hypertension…" : ""}
                onChange={e => set("chronic_conditions", e.target.value)}
                onFocus={e => editing && (e.target.style.borderColor = "var(--brand-primary)")}
                onBlur={e => e.target.style.borderColor = "var(--border-color)"}
              />
              {!editing && !form.chronic_conditions && (
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>None recorded</span>
              )}
            </Field>

          </Section>
        </div>
      </div>
    </PortalLayout>
  );
}